"use client";

import { useDeferredValue, useMemo, useState } from "react";
import { Notice, TextAreaField } from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  type CheckerLabels,
  CheckerReport,
} from "@/components/tools/reference-checkers/checker-report";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import {
  checkVancouverReferences,
  VANCOUVER_SOURCES,
  type VancouverReport,
  type VancouverRule,
} from "@/lib/citation-check-vancouver";
import { t, tPlural } from "@/lib/i18n/interpolate";

/** English strings. A translated content file overrides these through `ui`. */
export const CITATION_CHECKER_VANCOUVER_STRINGS = {
  inputLabel: "Vancouver reference list",
  inputPlaceholder:
    "1. Halpern SD, Ubel PA, Caplan AL. Solid-organ transplantation in HIV-infected patients. N Engl J Med. 2002 Jul 25;347(4):284-7.",
  inputHint:
    "One reference per line, or wrapped lines under their number. The check runs in your browser — nothing is uploaded.",
  trySample: "Try a sample",
  clear: "Clear",
  sample:
    '1. Halpern SD, Ubel PA, Caplan AL. Solid-organ transplantation in HIV-infected patients. N Engl J Med. 2002 Jul 25;347(4):284-7.\n2. Rose ME, Huerbin MB, Melick J, Marion DW, Palmer AM, Schiding JK, Kochanek PM, Graham SH. Regulation of interstitial excitatory amino acid concentrations after cortical contusion injury. Brain Res. 2002;935(1-2):40-6.\n3. Forooghian, F., Yeh, S., Faia, L. J., & Nussenblatt, R. B. "Uveitic Foveal Atrophy: Clinical Features and Associations." Arch. Ophthalmol. 2009 February; 127(2): 179-186.\n5. Zhang M, Holman CD, Price SD, Sanfilippo FM, Preen DB, Bulsara MK. Comorbidity and repeat admission to hospital for adverse drug reactions in older adults: retrospective cohort study. BMJ. 2009 Jan 7;338:a2752. doi:10.1136/bmj.a2752',
  emptyPrompt: "Paste a Vancouver reference list to check it.",
  results: "Results",
  summary: "{issues} across {entries}",
  issuesCount:
    "one:{count} likely issue to review|other:{count} likely issues to review",
  entriesCount: "one:{count} reference|other:{count} references",
  noIssues: "No likely issues found in {entries}.",
  noIssuesBody:
    "This is a first pass, not a guarantee. The checker reads the punctuation, order and abbreviations of each reference; it cannot tell whether a source exists, whether its details are right, or whether a journal abbreviation matches the NLM Catalog.",
  cleanCount:
    "one:{count} reference had no likely issues.|other:{count} references had no likely issues.",
  listHeading: "The list as a whole",
  entryLabel: "Reference {label}",
  confidence: { high: "Likely issue", medium: "Worth a look" },
  suggestionLabel: "Suggested:",
  copyFix: "Copy fix",
  copied: "Copied!",
  why: "Why: {source} →",
  sources: {
    nlm: "NLM sample references",
    citingMedicine: "Citing Medicine (NLM)",
    icmje: "ICMJE Recommendations",
  },
  ruleTitles: {
    unnumbered: "References are not numbered",
    "numbering-missing": "Some references have no number",
    "numbering-duplicate": "A reference number is used twice",
    "numbering-start": "The list does not start at 1",
    "numbering-gap": "Numbers are missing from the sequence",
    "numbering-order": "Numbers are out of sequence",
    "duplicate-entry": "The same source may be listed twice",
    "alphabetical-order": "The list looks alphabetical",
    "author-periods": "Periods in the initials",
    "author-comma": "Comma between surname and initials",
    "author-natural": "Initials before the surname",
    "author-and": "“and” or “&” before the last author",
    "et-al-threshold": "More than six authors listed",
    "et-al-early": "et al. after fewer than six names",
    "title-quotes": "Article title in quotation marks",
    "title-case": "Article title in Title Case",
    "journal-periods": "Periods in the journal abbreviation",
    "ieee-locators": "IEEE-style vol., no. and pp.",
    "locator-spacing": "Spaces in the date, volume and pages",
    "page-range": "Page range not shortened",
    "month-format": "Month not in NLM’s three-letter form",
    "doi-format": "DOI not written “doi: 10.…”",
    "terminal-period": "Reference does not end with a period",
  } satisfies Record<VancouverRule, string>,
  ruleMessages: {
    unnumbered:
      "Vancouver numbers references consecutively in the order they are first cited in the text. None of these references has a number.",
    "numbering-missing":
      "Every reference in a Vancouver list has a number. These entries, counting from the top, have none: {entries}.",
    "numbering-duplicate":
      "{number} appears more than once. Each number belongs to exactly one reference.",
    "numbering-start":
      "The first number is {first}. A complete list starts at 1 — ignore this if you pasted only part of your list.",
    "numbering-gap":
      "These numbers are missing: {numbers}. If a reference was removed, renumber the list and the citations in your text.",
    "numbering-order":
      "{number} comes after {previous}. A Vancouver list runs in numerical order, and the numbers follow the order of first citation.",
    "duplicate-entry":
      "References {first} and {second} look like the same source (identical text or the same DOI). Give each source one number and reuse it every time you cite it.",
    "alphabetical-order":
      "These references run A–Z by first author. Vancouver numbers references in the order they are first cited, so an alphabetical list usually means numbers were added to an author–date list.",
    "author-periods":
      "Vancouver writes initials with no periods or spaces after the surname: Smith JA, not Smith J. A.",
    "author-comma":
      "There is no comma between a surname and its initials in Vancouver — Smith JA, not Smith, J. A. Commas separate one author from the next.",
    "author-natural":
      "Vancouver puts the surname first, then the initials: Smith JA, not J. A. Smith.",
    "author-and":
      "Vancouver separates every name with a comma, with no “and” or “&” before the last author.",
    "et-al-threshold":
      "NLM’s sample references list the first six authors, then et al. This reference names {count}. NLM’s own database now lists every author, so follow your journal if it asks for all of them.",
    "et-al-early":
      "Only {count} names come before et al. NLM’s samples list six before et al. — three is the AMA rule. If the source has six or fewer authors, list them all.",
    "title-quotes":
      "Vancouver article titles take no quotation marks: Solid-organ transplantation in HIV-infected patients.",
    "title-case":
      "Vancouver titles are in sentence case — capitalize only the first word, proper nouns and abbreviations. Every main word of this title is capitalized.",
    "journal-periods":
      "Write {expected} rather than {found} — NLM journal abbreviations have no periods. Look abbreviations up in the NLM Catalog.",
    "ieee-locators":
      "vol., no. and pp. are IEEE labels. Vancouver writes the block as Year;Volume(Issue):Pages, as in 2002;347(4):284-7.",
    "locator-spacing":
      "The date, volume, issue and pages are set without spaces in Vancouver: {expected}, not {found}.",
    "page-range":
      "Citing Medicine drops the leading digits the last page repeats: {expected}, not {found} (123-125 becomes 123-5). Many journals print full ranges, so check yours.",
    "month-format":
      "Write {expected} rather than {found} — NLM writes a month as its first three letters, with no period.",
    "doi-format":
      "NLM’s samples write a DOI as “doi:”, a space and the DOI, and the reference ends with a period: {expected}",
    "terminal-period":
      "A Vancouver reference ends with a period — after the pages, the DOI or the PubMed ID. Only a reference that ends with a URL takes none.",
  } satisfies Record<VancouverRule, string>,
  checksHeading: "What this checker looks for",
  checks: [
    "Numbering in citation order, with no gaps, repeats or numbers out of sequence.",
    "Authors as Smith JA — surname, then initials with no periods or spaces — separated by commas with no “and”: the first six names, then et al.",
    "Article titles in sentence case, without quotation marks.",
    "Journal abbreviations without periods: N Engl J Med.",
    "The block Year Mon Day;Volume(Issue):Pages with no spaces, three-letter months and shortened page ranges (284-7).",
    "DOIs written doi: 10.xxxx, and a period at the end of each reference.",
    "The same source listed twice, and lists sorted A–Z instead of by first citation.",
  ],
  limits:
    "It does not check journal abbreviations against the NLM Catalog, and journals that follow the ICMJE recommendations still differ in small ways — some print full page ranges or list every author. Every finding is a likely issue to review, not a verdict; your list is never rewritten for you.",
} as const;

type Strings = typeof CITATION_CHECKER_VANCOUVER_STRINGS;

function reportLabels(
  report: VancouverReport,
  strings: Strings,
  locale: string,
  fmt: (value: number) => string
): CheckerLabels {
  const entries = tPlural(strings.entriesCount, locale, report.totalEntries, {
    count: fmt(report.totalEntries),
  });
  const issues = tPlural(strings.issuesCount, locale, report.totalFindings, {
    count: fmt(report.totalFindings),
  });
  const clean = report.entries.filter(
    (entry) => entry.findings.length === 0
  ).length;
  return {
    results: strings.results,
    summary:
      report.totalFindings === 0
        ? t(strings.noIssues, { entries })
        : t(strings.summary, { issues, entries }),
    noIssues: t(strings.noIssues, { entries }),
    noIssuesBody: strings.noIssuesBody,
    listHeading: strings.listHeading,
    entryLabel: strings.entryLabel,
    confidence: strings.confidence,
    suggestionLabel: strings.suggestionLabel,
    copyFix: strings.copyFix,
    copied: strings.copied,
    why: strings.why,
    cleanNote:
      report.totalFindings > 0 && clean > 0
        ? tPlural(strings.cleanCount, locale, clean, { count: fmt(clean) })
        : "",
  };
}

export default function CitationCheckerVancouverWidget() {
  const strings = useToolUi(CITATION_CHECKER_VANCOUVER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const deferred = useDeferredValue(text);
  const report = useMemo(
    () => (deferred.trim() ? checkVancouverReferences(deferred) : null),
    [deferred]
  );
  const sources = useMemo(
    () => ({
      nlm: { url: VANCOUVER_SOURCES.nlm, label: strings.sources.nlm },
      citingMedicine: {
        url: VANCOUVER_SOURCES.citingMedicine,
        label: strings.sources.citingMedicine,
      },
      icmje: { url: VANCOUVER_SOURCES.icmje, label: strings.sources.icmje },
    }),
    [strings]
  );

  return (
    <>
      <TextAreaField
        hint={strings.inputHint}
        id="vancouver-check-input"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        prose
        rows={10}
        value={text}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText(strings.sample)}
          type="button"
        >
          {strings.trySample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {report ? (
        <CheckerReport
          fmt={fmt}
          labels={reportLabels(report, strings, locale, fmt)}
          messages={strings.ruleMessages}
          report={report}
          sources={sources}
          titles={strings.ruleTitles}
        />
      ) : (
        <Notice>{strings.emptyPrompt}</Notice>
      )}
      <section className={`${CARD} space-y-3`}>
        <h2 className={WIDGET_H3}>{strings.checksHeading}</h2>
        <ul className="list-disc space-y-2 pl-5 text-muted-foreground text-sm leading-relaxed">
          {strings.checks.map((check) => (
            <li key={check}>{check}</li>
          ))}
        </ul>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.limits}
        </p>
      </section>
    </>
  );
}
